/**
 * Lightweight streaming text store.
 * Allows TextNode to push streaming tokens without updating React Flow node state
 * (which triggers expensive setNodes + saveNodes on every token).
 * TextFile reads from this store via subscription during generationStatus === 'loading'.
 */

type Listener = (text: string) => void

const streams = new Map<string, string>()
const listeners = new Map<string, Set<Listener>>()

export const streamingStore = {
  /** Push new text for a node (called by TextNode onToken) */
  set(nodeId: string, text: string) {
    streams.set(nodeId, text)
    const subs = listeners.get(nodeId)
    if (subs) {
      for (const fn of subs) fn(text)
    }
  },

  /** Get current text for a node */
  get(nodeId: string): string {
    return streams.get(nodeId) || ''
  },

  /** Subscribe to text updates (returns unsubscribe fn) */
  subscribe(nodeId: string, fn: Listener): () => void {
    let subs = listeners.get(nodeId)
    if (!subs) {
      subs = new Set()
      listeners.set(nodeId, subs)
    }
    subs.add(fn)
    return () => {
      subs!.delete(fn)
      if (subs!.size === 0) listeners.delete(nodeId)
    }
  },

  /** Clean up when streaming ends */
  clear(nodeId: string) {
    streams.delete(nodeId)
    listeners.delete(nodeId)
  },
}
